/**
 * Generic job-definition state/tooltip/payload derivation from a form's
 * descriptor list. See `FieldList.jsx` for the descriptor shape and
 * rendering; both operate on the same descriptors.
 *
 * A `jobForm` is `{ header?: {...}, body?: {...} }` — the (both optional)
 * named content regions `JobForm` renders, each `{ title, fields,
 * subsections? }` (a `subsection` is the same shape again, one level deep,
 * for a collapsible sub-group like DrugFlow's "Advanced Parameters").
 * They're named rather than an ordered list specifically so a *tab* can
 * say "put the fields I own into the header" (or body) without needing to
 * know anything about a job's own internal section count/order — see
 * `JobForm.jsx`'s `header`/`body` props and `mergeRegion`. The submit
 * button's own label/tooltip/title live entirely on the tab's `footer`
 * prop, not on `jobForm` at all — see `submitTooltipFromJobForm`. Every
 * OTHER derivation in this file — initial state, submit tooltip, resolve,
 * payload — only cares about the full set of fields a job has, not which
 * region declared them, so they all operate on the flat list
 * `allDescriptorsFromJobForm` produces; only `JobForm`'s rendering needs
 * the region structure itself.
 */

import { RESOLVERS } from './widgets';

function resolve(value, formState) {
    return typeof value === 'function' ? value(formState) : value;
}

/**
 * Converts a keyed field schema (a region's or subsection's own `fields`)
 * into the descriptor array `FieldList` and this module's other helpers
 * actually consume — each schema key becomes a descriptor's own `key`. A
 * `variant` entry's `options` get the same treatment recursively: each
 * option's `fields` is its own keyed schema, so it round-trips through
 * this function too.
 * @param {Object<string, object>} fields
 * @returns {Array<object>}
 */
export function descriptorsFromJobForm(fields) {
    return Object.entries(fields).map(([key, entry]) => {
        if (entry.widget !== 'variant' || !entry.options) return { key, ...entry };
        return {
            key,
            ...entry,
            options: Object.entries(entry.options).map(([value, option]) => ({
                value,
                label: option.label,
                fields: option.fields ? descriptorsFromJobForm(option.fields) : undefined,
            })),
        };
    });
}

/**
 * All descriptors one region (`header`/`body`) contributes,
 * including its subsections' — the one place that knows how to walk a
 * region's own shape.
 * @param {{ fields?: object, subsections?: Array<object> }} [region]
 * @returns {Array<object>}
 */
function regionDescriptors(region) {
    if (!region) return [];
    return [
        ...descriptorsFromJobForm(region.fields || {}),
        ...(region.subsections || []).flatMap((sub) => descriptorsFromJobForm(sub.fields || {})),
    ];
}

/**
 * Every descriptor a job's whole `jobForm` has, flattened across
 * `header`/`body` and their subsections, in render order. This is the one
 * place that knows a `jobForm` has exactly these two named regions —
 * everything else in this file just wants "all the fields."
 * @param {{ header?: object, body?: object }} jobForm
 * @returns {Array<object>}
 */
export function allDescriptorsFromJobForm(jobForm) {
    return [...regionDescriptors(jobForm.header), ...regionDescriptors(jobForm.body)];
}

function isVisible(descriptor, formState) {
    return descriptor.visible === undefined || resolve(descriptor.visible, formState);
}

/**
 * A value is "empty" if it's absent (`undefined`/`null`/`''`) or an array
 * with nothing in it — `![]` is `false` in plain JS, so array-valued fields
 * (e.g. the `entryList` widget) need this spelled out explicitly rather than
 * relying on a bare falsiness check. Deliberately NOT a bare falsiness check
 * on scalars either: `0` and `false` are legitimate values for number/
 * checkbox fields (e.g. DrugFlow's `seed: 0`), not "unfilled."
 * @param {*} value
 * @returns {boolean}
 */
function isEmpty(value) {
    if (Array.isArray(value)) return value.length === 0;
    return value === undefined || value === null || value === '';
}

/**
 * A descriptor is unmet when `required` is a function that says so, or —
 * the common case — `required: true` and the descriptor's own value is
 * empty. A plain `true` needs no per-descriptor predicate since "is this
 * field itself filled in" is exactly the same check for every descriptor;
 * `required` only needs to be a function for the rarer case where one
 * descriptor's requiredness depends on some other descriptor's value.
 * @param {object} descriptor
 * @param {object} formState
 * @returns {boolean}
 */
function isUnmet(descriptor, formState) {
    if (!descriptor.required) return false;
    if (typeof descriptor.required === 'function') return descriptor.required(formState);
    return isEmpty(formState[descriptor.key]);
}

/**
 * A `number` descriptor's `min`/`max` are enforced by `IncrementDecrementInput`
 * only for its +/- buttons — typed input bypasses them entirely, so a value
 * outside bounds can otherwise reach `buildRequestPayload` unchecked (e.g. a
 * negative `n_samples`, or a seed above DrugFlow's `np.random.seed` limit).
 * This mirrors `isUnmet` so out-of-bounds blocks submission the same
 * generic way an unmet `required` does, for every job form's number fields.
 * @param {object} descriptor
 * @param {object} formState
 * @returns {boolean}
 */
function isOutOfBounds(descriptor, formState) {
    if (descriptor.widget !== 'number') return false;
    const value = formState[descriptor.key];
    if (isEmpty(value)) return false;
    const num = Number(value);
    if (descriptor.min !== undefined && num < descriptor.min) return true;
    if (descriptor.max !== undefined && num > descriptor.max) return true;
    return false;
}

function boundsMessage(descriptor) {
    const { label, min, max } = descriptor;
    if (min !== undefined && max !== undefined) return `${label} must be between ${min} and ${max}.`;
    if (min !== undefined) return `${label} must be at least ${min}.`;
    return `${label} must be at most ${max}.`;
}

/**
 * Derives initial `formState` for a flat keyed fields schema — no
 * job-specific override needed unless a descriptor's initial value depends
 * on more than the schema itself, in which case that descriptor declares
 * `initialValueFrom(initData)` and it wins whenever it returns something
 * non-empty. Exported (rather than folded into `initialStateFromJobForm`)
 * so `JobForm.jsx` can use the same derivation for its own
 * `commonFields` — those aren't part of any job's `jobForm` at all, but
 * still need working defaults.
 * @param {Object<string, object>} fields
 * @param {object} [initData]
 * @returns {object}
 */
export function initialStateFromFields(fields, initData) {
    const result = {};
    for (const entry of descriptorsFromJobForm(fields)) {
        const fromInitData = entry.initialValueFrom?.(initData);
        if (fromInitData !== undefined && fromInitData !== '') {
            result[entry.key] = fromInitData;
        } else if (entry.default !== undefined) {
            result[entry.key] = entry.default;
        }
    }
    return result;
}

function initialStateFromRegion(region, initData) {
    if (!region) return {};
    return {
        ...initialStateFromFields(region.fields || {}, initData),
        ...(region.subsections || []).reduce(
            (acc, sub) => ({ ...acc, ...initialStateFromFields(sub.fields || {}, initData) }), {}
        ),
    };
}

/**
 * Derives a job's whole initial `formState` straight from its `jobForm`
 * schema — see `initialStateFromFields` for the per-field rule.
 * `formState` is always flat, regardless of how many regions/subsections a
 * job declares — regions are a rendering/grouping concern only.
 * @param {object} jobForm
 * @param {object} [initData]
 * @returns {object}
 */
export function initialStateFromJobForm(jobForm, initData) {
    return {
        ...initialStateFromRegion(jobForm.header, initData),
        ...initialStateFromRegion(jobForm.body, initData),
    };
}

/**
 * Evaluates each visible descriptor's `required` and number `min`/`max` in
 * array order and returns the first violation's blocking tooltip; otherwise
 * returns `enabledTooltip`.
 * @param {Array<object>} descriptors
 * @param {object} formState
 * @param {string} [enabledTooltip]
 * @returns {{ disabled: boolean, tooltip: string }}
 */
function submitTooltipFromDescriptors(descriptors, formState, enabledTooltip = '') {
    for (const descriptor of descriptors) {
        if (!isVisible(descriptor, formState)) continue;
        if (isUnmet(descriptor, formState)) {
            return { disabled: true, tooltip: descriptor.requiredMessage };
        }
        if (isOutOfBounds(descriptor, formState)) {
            return { disabled: true, tooltip: boundsMessage(descriptor) };
        }
    }
    return { disabled: false, tooltip: enabledTooltip };
}

/**
 * The generic submit-tooltip derivation shared by every job type: checks
 * every descriptor for an unmet `required`, falling back to `enabledTooltip`
 * once nothing blocks submission. `enabledTooltip` comes from the caller
 * (the embedding tab's `footer.tooltip`, via `JobForm.jsx`) rather than
 * from `jobForm` itself — submit's own label/tooltip/title are tab-owned,
 * the same way `header`'s job-type-picker/job-naming inputs are, since
 * different tabs embedding the same job want their own submit framing.
 * @param {object} jobForm
 * @param {object} formState
 * @param {string} [enabledTooltip]
 * @returns {{ disabled: boolean, tooltip: string }}
 */
export function submitTooltipFromJobForm(jobForm, formState, enabledTooltip = '') {
    return submitTooltipFromDescriptors(
        allDescriptorsFromJobForm(jobForm), formState, enabledTooltip
    );
}

/**
 * Extracts one descriptor's value from `formState`, running its widget's
 * `RESOLVERS[widget]` entry (see `widgets/index.js`) if one exists —
 * turning, e.g., a `pocket` descriptor's raw resSpec into the protein/
 * ligand structures a job actually needs. A widget with no resolver just
 * returns the raw formState value unchanged — this is the one place that
 * needs to know `RESOLVERS` even exists.
 *
 * This conversion deliberately does NOT happen in a job's own
 * `buildRequestPayload`, even though nothing stops it technically (that
 * method is already async, and could just as well import `App`/
 * `UserActions` and look a resSpec up itself). The reason is what the
 * conversion is a property OF: turning a `pocket` descriptor's raw
 * resSpec into `{ proteinPdb, ligandSdf }` is knowledge that belongs to
 * the `pocket` widget, not to DrugFlow specifically — any other job that
 * ever declares a `pocket` field needs the exact same lookup against the
 * live workspace. Keeping it here, keyed by widget type rather than job
 * type, means every such job gets it for free; pushing it into
 * `buildRequestPayload` would mean every job using that widget
 * reimplementing the same workspace lookup itself, and `buildRequestPayload`
 * taking on a dependency (`App`/`Compound`/`UserActions`) that has nothing
 * to do with its actual job of shaping ITS OWN request body.
 * @param {object} descriptor
 * @param {object} formState
 * @returns {Promise<{ value?: *, error?: string }>}
 */
async function resolveDescriptor(descriptor, formState) {
    const value = formState[descriptor.key];
    const resolver = RESOLVERS[descriptor.widget];
    return resolver ? resolver(value) : { value };
}

/**
 * Derives a job's whole `formState` into `buildRequestPayload`'s input, in
 * one recursive async pass over its flat descriptor list (see
 * `allDescriptorsFromJobForm`), dropping tab-level bookkeeping and blank
 * fields along the way. This is NOT the request payload itself —
 * `buildRequestPayload` still does every bit of job-specific reshaping
 * (e.g. combining a `molecule_size` variant's own sub-fields into
 * DrugFlow's single string) from whatever this returns.
 *
 * Per visible descriptor: skip it outright if `omitFromPayload` (form-level
 * bookkeeping — e.g. `chosenJobType` — a job's request must never see, and
 * never worth resolving just to discard, so every job can safely spread
 * the rest of its properties through without separately remembering to
 * strip tab-only fields itself); a `variant` descriptor contributes its
 * own mode value plus — flattened into this SAME result, not nested —
 * whichever sub-fields the currently selected option lists, recursing the
 * same way (so a sub-field gets the same resolve/omit/empty treatment
 * too), since those live as ordinary `formState` keys right alongside the
 * variant's own key (see `widgets/simpleWidgets.jsx`'s `VariantWidget`);
 * every other descriptor's value comes from `resolveDescriptor` — a
 * resolver failure is collected into `errors` and that descriptor is
 * skipped, the same way for every job — then, unless the (possibly
 * just-resolved) value is empty (see `isEmpty` — `undefined`/`''`/an empty
 * array; deliberately NOT a bare falsiness check, since `0` and `false`
 * are legitimate values, not "unfilled"), it's included as-is.
 *
 * `result`/`errors` are accumulator params (rather than always starting
 * fresh) specifically so a recursive call, for a variant's sub-fields,
 * writes into the SAME objects the top-level call started with, instead of
 * needing a merge step after each recursion returns; a caller never has to
 * pass them — they default to an empty object/array, and only the
 * recursive calls below ever pass them explicitly.
 * @param {Array<object>} descriptors
 * @param {object} formState
 * @param {object} [result]
 * @param {string[]} [errors]
 * @returns {Promise<{ values: object, errors: string[] }>}
 */
export async function deriveFormValues(descriptors, formState, result = {}, errors = []) {
    for (const descriptor of descriptors) {
        if (!isVisible(descriptor, formState)) continue;
        if (descriptor.omitFromPayload) continue;
        if (descriptor.widget === 'variant' && descriptor.options) {
            const mode = formState[descriptor.key] ?? descriptor.options[0]?.value;
            if (mode !== undefined && mode !== '') result[descriptor.key] = mode;
            const current = descriptor.options.find((option) => option.value === mode);
            if (current?.fields?.length > 0) {
                await deriveFormValues(current.fields, formState, result, errors);
            }
            continue;
        }
        const { value, error } = await resolveDescriptor(descriptor, formState);
        if (error) {
            errors.push(error);
            continue;
        }
        if (isEmpty(value)) continue;
        result[descriptor.key] = value;
    }
    return { values: result, errors };
}
